import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

import { getIsPrivateRequestEnabled, getIsTourOfferPurchasable, getSelectedVariationDepartureBoundaries } from 'selectors/TourV2/tourV2OfferPageSelectors'
import { getShareModalConfig, ShareModalConfig } from 'lib/customer/getShareModalConfig'
import { DMY_CASUAL_LONG_FORMAT, LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD, TourV2ContentSections } from 'constants/tours'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Image from 'components/Common/Image'
import TextLink from 'components/Luxkit/TextLink'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import ShareModal from 'components/Common/Modals/ShareModal'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import TourBookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/TourBookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import CurrencyContext from 'contexts/currencyContext'
import DepositsAvailableTag from 'components/Common/SecureWithDeposit/DepositsAvailableTag'
import { isTourV2DepositDateAllowed, isTourV2DepositEnabledForCurrency } from 'lib/tours/isTourV2DepositAllowed'
import Group from 'components/utils/Group'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import UltraLuxUSPs from '../UltraLux/UltraLuxUSPs'
import ProductPaletteProvider, { ProductPalette } from 'contexts/ProductPaletteContext'
import ModalContext from 'contexts/ModalContext'
import PrivateRequestFormModal from 'components/OfferPage/TourV2/PrivateRequest/PrivateRequestFormModal'
import TourV2USP from 'components/OfferPage/TourV2/TourV2USP'
import { isUltraLuxTour } from 'lib/offer/offerUtils'
import TourV2UrgencyTags from 'components/Tours/TourV2UrgencyTags'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import useTourDiscount from 'hooks/TourV2/useTourDiscount'
import TourV2HostsSponsors from 'components/OfferPage/TourV2/TourV2HostsSponsors'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TourV2OverviewPanel from 'components/OfferPage/TourV2/TourV2OverviewPanel'
import config from 'constants/config'
import ExportMediaContentButton from 'components/Common/ExportMediaContentButton/ExportMediaContentButton'
import { useAppSelector } from 'hooks/reduxHooks'
import { buildAgentHubQuotingURL } from 'lib/agentHub/quoting'

const GridContainer = styled.div`
  display: grid;
  grid-template:
    "top-bar"
    "content"
    "overview"
    / 1fr;
  grid-row-gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    grid-template:
      "top-bar top-bar"
      "content overview"
      / 1fr minmax(min-content, ${rem(400)});
    grid-row-gap: ${rem(8)};
    grid-column-gap: ${rem(32)};
  }

  > .top-bar-area {
    grid-area: top-bar;
  }

  > .content-area {
    grid-area: content;
  }

  > .overview-area {
    grid-area: overview;
  }
`

const ContentGridContainer = styled.div`
  display: grid;
  grid-template:
    "title" min-content
    "details" min-content
    "review" min-content
    "overview-panel" min-content
    "description" min-content
    / 1fr;

  > .title-area {
    grid-area: title;
  }
  > .details-area {
    grid-area: details;
    margin-top: ${rem(8)};
  }
  > .review-area {
    grid-area: review;
    margin-top: ${rem(12)};
    &:empty {
      display: none;
    }
  }
  > .overview-panel-area {
    grid-area: overview-panel;
    margin-top: ${rem(16)};
    &:empty {
      display: none;
    }
  }
  > .description-area {
    grid-area: description;
    margin-top: ${rem(16)};
  }
`

const OperatorLogoImage = styled(Image)`
  max-width: 100%;
  max-height: 100%;
`

const OperatorLogoContainer = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: block;
    flex-shrink: 0;
    max-height: ${rem(75)};
    max-width: ${rem(150)};
    align-self: center;
  }
`

const Description = styled(MarkdownRender)`
  line-height: ${rem(24)};
`

const DetailItemsContainer = styled(Group)`
  &:empty {
    display: none;
  }
`

const VerticalDivider = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: block;
    height: ${rem(24)};
    width: ${rem(1)};
    background-color: ${props => props.theme.palette.neutral.default.six};
  }
`

interface MappedProps {
  latestDeparture: Tours.TourV2OfferDeparture
  variation: Tours.TourV2OfferVariation
  offer: Tours.TourV2Offer
  isOfferPurchasable: boolean
  isPrivateRequestEnabled: boolean
}

interface Props {
  cheapestPurchasableOption: Tours.TourV2OfferPurchasableOption
  onView: (ref: string) => void
  shareModalConfig: ShareModalConfig
  buttonPaletteValue: ProductPalette
}

function TourV2Overview(props: Props & MappedProps) {
  const {
    cheapestPurchasableOption,
    isOfferPurchasable,
    isPrivateRequestEnabled,
    latestDeparture,
    offer,
    onView,
    shareModalConfig,
    variation,
    buttonPaletteValue,
  } = props
  const currentCurrency = useContext(CurrencyContext)

  // operator
  const operatorName = offer.operatedBy
  const operatorLogo = offer.brand?.logoImage

  // Activity level
  const activityLevel = offer.activityLevel
  // Discount on tour
  const { hasSomeDiscount, allIsDiscounted } = useTourDiscount(offer)

  const availableDepartures = useMemo(() => {
    if (!offer.departures) return []
    return Object.values(offer.departures)
      .filter(e => e.status === 'available' && e.fkVariationId === variation.id)
  }, [offer.departures, variation.id])

  // last available booking date
  const { lastAvailableBookingDate, showSecureWithDepositTag } = useMemo(() => {
    if (latestDeparture.status !== 'available') {
      return {
        lastAvailableBookingDate: undefined,
        showSecureWithDepositTag: false,
      }
    }
    const dateFormat = availableDepartures.length === 1 ? DMY_CASUAL_LONG_FORMAT : LONG_MONTH_LONG_YEAR
    const lastDate = moment(latestDeparture.startDate).format(dateFormat)
    return {
      lastAvailableBookingDate: lastDate,
      showSecureWithDepositTag: offer.depositType !== 'noDeposit' && isTourV2DepositEnabledForCurrency(currentCurrency) && isTourV2DepositDateAllowed(lastDate, offer.depositThresholds?.numberOfDays ?? TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD),
    }
  }, [latestDeparture, currentCurrency, offer.depositType, offer.depositThresholds?.numberOfDays, availableDepartures])

  const isTourReviewsVisible = offer.reviewsRating > 0 && offer.reviewsTotal > 0

  const {
    description,
    startLocation,
    endLocation,
    hasTheSameStartAndEndLocation,
  } = useTourV2VariationDetails(variation)

  const shareConfig = getShareModalConfig(shareModalConfig)

  const scrollToDepartures = useCallback(
    () => {
      onView(TourV2ContentSections.DEPARTURES)
    },
    [onView],
  )

  const scrollToItinerary = useCallback(() => { onView(TourV2ContentSections.ITINERARY) }, [onView])
  const scrollToReviews = useCallback(() => { onView(TourV2ContentSections.REVIEWS) }, [onView])
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isExportMediaEnabled = config.EXPORT_MEDIA_CONTENT_ENABLED
  const isAgentHubEnabled = config.agentHub.isEnabled
  const mediaExportURL = isAgentHubEnabled ? buildAgentHubQuotingURL(offer.id, { currency, region }) : undefined

  const showModal = useContext(ModalContext)
  const requestPrivateTour = useCallback(() => {
    showModal(<PrivateRequestFormModal variations={offer.variations} tourId={offer.id} />)
  }, [showModal, offer])

  const hasTourExperiences = useMemo(
    () => variation.itinerary.some(itinerary => itinerary.experiences.length > 0),
    [variation.itinerary],
  )

  const departureDateText = useMemo(() => {
    if (availableDepartures) {
      return (availableDepartures.length === 1 ? 'Tour departs: ' : 'Book up until')
    }
  }, [availableDepartures])

  return (
    <ProductPaletteProvider palette={buttonPaletteValue}>
      <Group direction="vertical" gap={20}>
        <GridContainer>
          <Group
            className="top-bar-area"
            direction="horizontal"
            horizontalAlign="space-between"
            verticalAlign="center"
            gap={8}
          >
            <LabelGroup>
              {showSecureWithDepositTag && <DepositsAvailableTag
                offerType={offer.type}
                depositAmountPercentage={offer.depositAmount}
                depositType={offer.depositType}
                depositThresholds={offer.depositThresholds}
              />}
              <TourV2UrgencyTags offer={offer}/>
            </LabelGroup>
            <CSSBreakpoint min="tablet">
              <Group direction="horizontal" verticalAlign="center" gap={8}>
                {isExportMediaEnabled && <ExportMediaContentButton size="small" target="_blank" href={mediaExportURL}/>}
                <ShareModal shareConfig={shareConfig}>
                  <ShareWithFriendsButton size="small" />
                </ShareModal>
                <TripGuard>
                  <TourBookmarkButton offer={offer} selectedVariation={variation} />
                </TripGuard>
              </Group>
            </CSSBreakpoint>
          </Group>
          <ContentGridContainer className="content-area">
            <ImmersiveSaveButtonPortal>
              <TourBookmarkButton offer={offer} selectedVariation={variation} />
            </ImmersiveSaveButtonPortal>
            <Group
              className="title-area"
              data-testid="tourV2-offer-overview"
              direction="horizontal"
              horizontalAlign="space-between"
              gap={8}
            >
              <VerticalSpacer gap={8}>
                <BodyText variant="large">
                  {hasTheSameStartAndEndLocation &&
                    <span>Starts and ends in <b>{endLocation}</b></span>
                  }
                  {!hasTheSameStartAndEndLocation && <span>
                    Starts in <b>{startLocation}</b>. Ends in <b>{endLocation}</b>
                  </span>}
                </BodyText>
                <Heading as="h1" variant="heading2">
                  {variation.name}
                </Heading>
              </VerticalSpacer>
              {operatorLogo && <OperatorLogoContainer>
                <OperatorLogoImage
                  id={operatorLogo.id}
                  dpr={2}
                  quality="eco"
                  width={150}
                  alt={operatorLogo.title}
                  aria-hidden="true"
                />
              </OperatorLogoContainer>}
            </Group>
            <DetailItemsContainer direction="vertical" desktopDirection="horizontal" desktopGap={16} gap={4} className="details-area">
              {operatorName && <BodyText variant="large"><span>Operated by: <b>{operatorName}</b></span></BodyText>}
              <VerticalDivider />
              {lastAvailableBookingDate &&
                <BodyText variant="large">
                  {departureDateText} &nbsp;
                  <TextLink variant="default" onClick={scrollToDepartures}>
                    {lastAvailableBookingDate}
                  </TextLink>
                </BodyText>}
            </DetailItemsContainer>
            <div className="review-area">
              {isTourReviewsVisible && (<NumberRating
                link
                inlineLabel
                variant="small"
                total={offer.reviewsTotal}
                rating={offer.reviewsRating}
                onReviewSubTitleClick={scrollToReviews}
              />)}
            </div>
            <div className="overview-panel-area">
              <CSSBreakpoint max="mobile">
                <TourV2OverviewPanel
                  offerId={offer.id}
                  activityLevel={activityLevel}
                  isPrivateRequestEnabled={isPrivateRequestEnabled}
                  purchasableOption={cheapestPurchasableOption}
                  showSecureWithDepositTag={showSecureWithDepositTag}
                  allIsDiscounted={allIsDiscounted}
                  hasSomeDiscount={hasSomeDiscount}
                  isOfferPurchasable={isOfferPurchasable}
                  hasTourExperiences={hasTourExperiences}
                  offerType={offer.type}
                  productType={offer.productType}
                  variation={variation}
                  scrollToDepartures={scrollToDepartures}
                  scrollToItinerary={scrollToItinerary}
                  guideType={offer.guideType}
                />
              </CSSBreakpoint>
            </div>
            <div className="description-area">
              <Description
                  fontSize="medium"
                  fontWeight="normal"
                  type="compact"
                  content={description}
                />
            </div>
          </ContentGridContainer>
          <div className="overview-area">
            <CSSBreakpoint min="tablet">
              <TourV2OverviewPanel
                offerId={offer.id}
                activityLevel={activityLevel}
                isPrivateRequestEnabled={isPrivateRequestEnabled}
                purchasableOption={cheapestPurchasableOption}
                showSecureWithDepositTag={showSecureWithDepositTag}
                allIsDiscounted={allIsDiscounted}
                hasSomeDiscount={hasSomeDiscount}
                isOfferPurchasable={isOfferPurchasable}
                hasTourExperiences={hasTourExperiences}
                offerType={offer.type}
                productType={offer.productType}
                variation={variation}
                scrollToDepartures={scrollToDepartures}
                scrollToItinerary={scrollToItinerary}
                guideType={offer.guideType}
              />
            </CSSBreakpoint>
            {(offer.hosts.length > 0 || offer.sponsors.length > 0) &&
              <TourV2HostsSponsors hosts={offer.hosts} sponsors={offer.sponsors} />
            }
          </div>
        </GridContainer>
        {isUltraLuxTour(offer) && <UltraLuxUSPs data-testid="tourV2-ultra-lux-usps" onRequestPrivateTour={requestPrivateTour} isPrivateRequest={variation.isPrivateRequest}/>}
        {!isUltraLuxTour(offer) && <TourV2USP onRequestPrivateTour={requestPrivateTour} isPrivateRequest={isPrivateRequestEnabled}/>}
      </Group>
    </ProductPaletteProvider>
  )
}

const mapOfferPageStateToProps = (offerPageState: OfferPageState, appState: App.State): MappedProps => {
  const { latestDeparture } = getSelectedVariationDepartureBoundaries(offerPageState)
  return {
    latestDeparture,
    offer: offerPageState.tourV2Offer,
    variation: offerPageState.selectedTourV2OfferVariation,
    isOfferPurchasable: getIsTourOfferPurchasable(offerPageState),
    isPrivateRequestEnabled: getIsPrivateRequestEnabled(offerPageState, appState),
  }
}

export default withOfferPageAndReduxState(mapOfferPageStateToProps)(TourV2Overview)
